import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocation, useParams } from 'react-router-dom'
import { useLang } from '_metronic/i18n/Metronici18n'
import { UIContext } from 'Context/UI'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import moment from 'moment'
import 'moment-timezone'
import * as Yup from 'yup'

const INITIAL_STATE: { [key: string]: any } = {
	title: '',
	type: 'percentage' as 'percentage' | 'fixed',
	value: '',
	code: '',
	amount: '',
	startDate: moment()
		.set({
			hours: 0,
			minutes: 0,
			seconds: 0,
		})
		.tz('Asia/Bangkok')
		.toISOString(),
	endDate: moment()
		.set({
			hours: 23,
			minutes: 59,
			seconds: 59,
		})
		.add({
			days: 7,
		})
		.tz('Asia/Bangkok')
		.toISOString(),
	description: '',
	featurePicturePath: null as File | null,
}

const COUPON_TYPES: any[] = [
	{
		labelEn: 'Percentage',
		labelTh: 'เปอร์เซ็นต์',
		value: 'percentage',
	},
	{
		labelEn: 'Fixed',
		labelTh: 'ส่วนลดเป็นเงินสด',
		value: 'fixed',
	},
]

const ViewModel = () => {
	const lang = useLang()
	const navigate = useNavigate()
	const location = useLocation()
	const { uuId } = useParams<{ uuId: string }>()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)

	const validationSchema = Yup.object().shape({
		title: Yup.string().required(lang === 'en' ? 'Title is required' : 'กรุณากรอกชื่อคูปอง'),
		type: Yup.string()
			.min(1, lang === 'en' ? 'Type is required' : 'กรุณาเลือกประเภท')
			.test(
				'type',
				lang === 'en' ? 'Type is required' : 'กรุณาเลือกประเภท',
				value => value === 'percentage' || value === 'fixed'
			)
			.required(lang === 'en' ? 'Type is required' : 'กรุณาเลือกประเภท'),
		value: Yup.string()
			.test(
				'value',
				lang === 'en' ? 'Value must be greater than 0' : 'มูลค่าต้องมากกว่า 0',
				value => {
					if (value !== undefined) {
						return value.length > 0 && parseFloat(value) > 0
					} else {
						return false
					}
				}
			)
			.required(lang === 'en' ? 'Value is required' : 'กรุณากรอกมูลค่าของคูปอง'),
		code: Yup.string().required(lang === 'en' ? 'Code is required' : 'กรุณากรอกรหัสคูปอง'),
		amount: Yup.string()
			.test(
				'value',
				lang === 'en' ? 'Amount must be greater than 0' : 'จำนวนต้องมากกว่า 0',
				value => {
					if (value !== undefined) {
						return value.length > 0 && parseFloat(value) > 0
					} else {
						return false
					}
				}
			)
			.required(lang === 'en' ? 'Amount is required' : 'กรุณากรอกจำนวน'),
		startDate: Yup.string().required(
			lang === 'en' ? 'Start Date is required' : 'กรุณาเลือกวันเริ่มต้น'
		),
		endDate: Yup.string().required(lang === 'en' ? 'End Date is required' : 'กรุณาเลือกวันสิ้นสุด'),
	})

	const isCreate = location.pathname.includes('create')
	const pageTitle = isCreate
		? lang === 'en'
			? 'Create Coupon'
			: 'สร้างคูปอง'
		: lang === 'en'
		? 'Edit Coupon'
		: 'แก้ไขคูปอง'
	const [formState, setFormState] = useState(INITIAL_STATE)
	const [formError, setFormError] = useState<{ [key: string]: string }>({})

	const couponTypeOptions = COUPON_TYPES.map(type => ({
		label: lang === 'en' ? type.labelEn : type.labelTh,
		value: type.value,
	}))

	const setupUI = () => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)
	}

	const resetUI = () => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)
	}

	const onChangeFormState = (key: string, value: any) => {
		if (['startDate', 'endDate'].includes(key)) {
			if (value) {
				setFormState({
					...formState,
					[key]: moment(value).tz('Asia/Bangkok').toISOString(),
				})
			} else {
				setFormState({
					...formState,
					[key]: value,
				})
			}
		} else {
			setFormState({
				...formState,
				[key]: value,
			})
		}
	}

	const onCancel = () => {
		navigate('/marketplace/coupons')
	}

	const onSubmit = () => {
		validationSchema
			.validate(formState, { abortEarly: false })
			.then(() => {
				setFormError({})

				if (isCreate) {
					Swal.fire({
						title: lang === 'en' ? 'Create Coupon' : 'สร้างคูปอง',
						text:
							lang === 'en'
								? 'Are you sure to create this coupon?'
								: 'คุณต้องการสร้างคูปองนี้ใช่หรือไม่?',
						icon: 'warning',
						showCancelButton: true,
						cancelButtonText: lang === 'en' ? 'Cancel' : 'ยกเลิก',
						confirmButtonText: lang === 'en' ? 'Confirm' : 'ยืนยัน',
					}).then(result => {
						if (result.isConfirmed) {
							toast.success(lang === 'en' ? 'Create coupon successfully' : 'สร้างคูปองสำเร็จ')
							navigate('/marketplace/coupons')
						}
					})
				} else if (!isCreate && uuId) {
					Swal.fire({
						title: lang === 'en' ? 'Update Coupon' : 'แก้ไขคูปอง',
						text:
							lang === 'en'
								? 'Are you sure to update this coupon?'
								: 'คุณต้องการแก้ไขคูปองนี้ใช่หรือไม่?',
						icon: 'warning',
						showCancelButton: true,
						cancelButtonText: lang === 'en' ? 'Cancel' : 'ยกเลิก',
						confirmButtonText: lang === 'en' ? 'Confirm' : 'ยืนยัน',
					}).then(result => {
						if (result.isConfirmed) {
							toast.success(lang === 'en' ? 'Update coupon successfully' : 'อัพเดทคูปองสำเร็จ')
							navigate('/marketplace/coupons')
						}
					})
				}
			})
			.catch(err => {
				const error: { [key: string]: string } = {}
				err.inner.forEach((e: any) => {
					error[e.path] = e.message
				})
				setFormError(error)
			})
	}

	useEffect(() => {
		setupUI()

		return () => {
			resetUI()
		}
		// eslint-disable-next-line
	}, [])

	return {
		pageTitle,
		couponTypeOptions,
		formState,
		formError,
		onChangeFormState,
		onCancel,
		onSubmit,
	}
}

export default ViewModel
